import { OP_CODES } from "./constants";
/**
 * Pull transactions from Toncenter and decode any deposit/withdraw log
 * out-messages.
 */
export async function scanPoolTransactions(client, opts) {
    const max = opts.maxTransactions ?? 100;
    // Build params: only include lt/hash when both are present. The toncenter
    // SDK's getTransactions does `Buffer.from(hash, 'base64')` which silently
    // corrupts hex-encoded hashes — pass them as base64 instead. archival:true
    // is required to see transactions older than ~1h on testnet, but causes 422
    // on accounts with very few txs, so we omit it on the first page request.
    const params = { limit: max };
    if (opts.fromLt !== undefined && opts.fromHash !== undefined) {
        params.lt = opts.fromLt.toString();
        params.hash = Buffer.from(opts.fromHash, "hex").toString("base64");
        params.archival = true;
    }
    const txs = await client.getTransactions(opts.pool, params);
    const events = [];
    let lastLt;
    let lastHash;
    for (const tx of txs) {
        lastLt = BigInt(tx.lt.toString());
        lastHash = tx.hash().toString("hex");
        for (const out of tx.outMessages.values()) {
            if (out.info.type !== "external-out")
                continue;
            const event = decodeLogOutMessage(out.body, {
                txHash: lastHash,
                timestamp: Number(tx.now),
            });
            if (event)
                events.push(event);
        }
    }
    return { events, lastLt, lastHash };
}
function decodeLogOutMessage(body, ctx) {
    try {
        const slice = body.beginParse();
        if (slice.remainingBits < 32)
            return null;
        const op = slice.loadUint(32);
        if (op !== OP_CODES.LOG_DEPOSIT)
            return null;
        // Layout (must match `pool_native.tolk` :: emit_deposit_log):
        //   uint32 op
        //   uint256 commitment
        //   uint32 leaf_index
        //   uint64 timestamp
        const commitment = slice.loadUintBig(256);
        const leafIndex = slice.loadUint(32);
        const timestamp = slice.loadUint(64);
        return {
            commitment,
            leafIndex,
            timestamp,
            txHash: ctx.txHash,
        };
    }
    catch {
        // Not a log message we recognise; skip.
        return null;
    }
}
/**
 * Decode a `LOG_WITHDRAW` external-out message body. Layout MUST match
 * `pool_native.tolk :: WithdrawLog` byte-for-byte and the relayer indexer's
 * decode loop at `apps/relayer/src/libs/Indexer.ts :: indexOnce`.
 *
 *   uint32  op = 0xded057a2
 *   uint256 nullifierHash
 *   uint256 recipientField  (addressToField(core.recipient))
 *   uint256 relayerField    (addressToField(core.relayer))
 *   coins   fee
 *   uint64  timestamp
 *
 * Returns `null` for any body that doesn't carry the expected op-code or
 * fails to deserialize cleanly — caller iterates over all external-out
 * messages and filters.
 */
export function decodeLogWithdrawOutMessage(body, ctx) {
    try {
        const slice = body.beginParse();
        if (slice.remainingBits < 32)
            return null;
        const op = slice.loadUint(32);
        if (op !== OP_CODES.LOG_WITHDRAW)
            return null;
        const nullifierHash = slice.loadUintBig(256);
        const recipientField = slice.loadUintBig(256);
        const relayerField = slice.loadUintBig(256);
        const fee = slice.loadCoins();
        const timestamp = slice.loadUint(64);
        return {
            nullifierHash,
            recipientField,
            relayerField,
            fee,
            timestamp,
            txHash: ctx.txHash,
        };
    }
    catch {
        return null;
    }
}
//# sourceMappingURL=indexer.js.map